import { useState } from 'react';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { siteName, siteDescription, brandInfo, contactInfo, siteUrl } from '@/mocks/site';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: `关于我们 — ${siteName}`,
  description: brandInfo.mission,
  url: `${siteUrl}/about`,
  isPartOf: {
    '@type': 'WebSite',
    name: siteName,
    url: siteUrl,
  },
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: siteName,
  url: siteUrl,
  description: siteDescription,
  slogan: brandInfo.slogan,
  foundingDate: brandInfo.founded,
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: contactInfo.email,
  },
  address: {
    '@type': 'PostalAddress',
    addressLocality: '上海',
    addressCountry: 'CN',
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
    { '@type': 'ListItem', position: 2, name: '关于我们', item: `${siteUrl}/about` },
  ],
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    const formData = new FormData(form);
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9u51olca23r0tr1aqp0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后重试。');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络连接后重试。');
    }
  };

  return (
    <>
      <SEO
        title={`关于我们 — ${siteName}`}
        description={brandInfo.mission}
        keywords="关于我们,中文字幕 日韩,日韩影视,字幕资源,品牌介绍"
        canonicalUrl={`${siteUrl}/about`}
        schema={{ ...aboutSchema, ...orgSchema, ...breadcrumbSchema }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          <div className="px-4 md:px-6 pb-14">
            <section className="mb-16">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
                关于 {siteName}
              </h1>
              <p className="text-lg text-foreground-600 mb-8">{brandInfo.slogan}</p>
              <p className="text-foreground-700 leading-relaxed mb-4">{brandInfo.mission}</p>
              <p className="text-foreground-700 leading-relaxed">{brandInfo.story}</p>
            </section>

            <section className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">我们的优势</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { icon: 'ri-shield-check-line', title: '资源精选', desc: '严格筛选高质量字幕资源，确保翻译准确、时间轴精准' },
                  { icon: 'ri-timer-flash-line', title: '更新及时', desc: '热门剧集播出后24-48小时内同步更新字幕资源' },
                  { icon: 'ri-user-heart-line', title: '用户至上', desc: '以用户体验为核心，持续优化导航与使用教程' },
                ].map((item) => (
                  <div key={item.title} className="p-6 rounded-2xl bg-background-100">
                    <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-500 mb-4">
                      <i className={`${item.icon} text-xl`}></i>
                    </span>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">用户口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { name: '日剧迷小王', text: '自从发现了这个平台，再也不用到处找字幕了！更新速度超快，翻译质量也很棒，强烈推荐给所有日韩影视爱好者。', rating: 5 },
                  { name: '韩剧达人', text: '界面简洁清晰，分类合理，找资源特别方便。字幕教程也很实用，新手也能轻松上手。真的太良心了！', rating: 5 },
                  { name: '动漫爱好者', text: '日韩番剧的字幕资源很全，新番更新也很及时。站长的推荐文章质量很高，看了好几篇都很有收获。', rating: 5 },
                  { name: '字幕爱好者小李', text: '作为一个字幕爱好者，看到有人这么用心地整理字幕资源真的很感动。希望平台越做越好！', rating: 5 },
                ].map((review) => (
                  <div key={review.name} className="p-6 rounded-2xl bg-background-100">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <span key={i} className="w-4 h-4 flex items-center justify-center text-accent-500">
                          <i className="ri-star-fill text-sm"></i>
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">{review.text}</p>
                    <p className="text-xs font-medium text-foreground-500">— {review.name}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">联系我们</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-500 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                      <p className="text-sm text-foreground-600">{contactInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-500 flex-shrink-0">
                      <i className="ri-wechat-line"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h3>
                      <p className="text-sm text-foreground-600">{contactInfo.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-500 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">地址</h3>
                      <p className="text-sm text-foreground-600">{contactInfo.address}</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-2xl bg-background-100">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">给我们留言</h3>
                  <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-4">
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                      <textarea
                        id="about-message"
                        name="message"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                    </div>
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />
                    <button
                      type="submit"
                      className="w-full py-3 rounded-xl bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      发送留言
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">{formMessage}</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formMessage}</p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}